import { Component, type ErrorInfo, type ReactNode } from "react";
import { writeClipboard } from "../utils/clipboard";
import { STORAGE_KEYS } from "../utils/storageKeys";
import { isChunkLoadError } from "./chunkError";
import type { Locale } from "./i18n";

const LOCALE_STORAGE_KEY = STORAGE_KEYS.locale;

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
  info: ErrorInfo | null;
  copied: boolean;
  /** The clipboard refused or is absent: fall back to a selectable report. */
  copyFailed: boolean;
}

/**
 * Localized recovery copy. A class component cannot call the useT hook, and
 * the boundary must keep working even when the provider tree itself crashed,
 * so it resolves the locale directly from the same persisted preference the
 * LocaleProvider uses. The diagnostic JSON report stays English by design.
 */
interface RecoveryCopy {
  eyebrow: string;
  title: string;
  lede: string;
  reload: string;
  reset: string;
  copyReport: string;
  copied: string;
  /** Said when the copy did not land, above the selectable report itself. */
  copyFailed: string;
  /** Accessible name of the fallback field holding the report. */
  reportLabel: string;
}

const COPY: Record<Locale, RecoveryCopy> = {
  en: {
    eyebrow: "Unexpected error",
    title: "That didn't go to plan.",
    lede: "MIME Smith hit an unexpected error. Your data did not leave the device. Reload the page. If it happens again, clear the saved settings or copy the error report to pass on.",
    reload: "Reload",
    reset: "Clear data & reload",
    copyReport: "Copy error report",
    copied: "Copied",
    copyFailed:
      "This browser would not let the page write to the clipboard. Select the report below and copy it by hand.",
    reportLabel: "Error report",
  },
  de: {
    eyebrow: "Unerwarteter Fehler",
    title: "Das lief nicht nach Plan.",
    lede: "MIME Smith ist auf einen unerwarteten Fehler gestoßen. Deine Daten haben das Gerät nicht verlassen. Lade die Seite neu. Tritt der Fehler erneut auf, lösche die gespeicherten Einstellungen oder kopiere den Fehlerbericht, um ihn weiterzugeben.",
    reload: "Neu laden",
    reset: "Daten löschen & neu laden",
    copyReport: "Fehlerbericht kopieren",
    copied: "Kopiert",
    copyFailed:
      "Dieser Browser hat der Seite den Zugriff auf die Zwischenablage verweigert. Markiere den Bericht unten und kopiere ihn von Hand.",
    reportLabel: "Fehlerbericht",
  },
};

/**
 * Mirrors LocaleProvider's resolution: the persisted explicit choice wins,
 * then the browser language, then English. Reads storage on every render so
 * the crash screen honours the preference without any React context.
 */
function resolveLocale(): Locale {
  try {
    const v = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (v === "en" || v === "de") return v;
  } catch {
    /* storage unavailable (private mode) */
  }
  const nav = (
    typeof navigator !== "undefined" ? (navigator.language ?? "") : ""
  ).toLowerCase();
  if (nav.startsWith("de")) return "de";
  return "en";
}

/** The new UI's own last-resort recovery screen. No legacy dependencies. */
export class ErrorBoundaryNext extends Component<Props, State> {
  state: State = { error: null, info: null, copied: false, copyFailed: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[mime-smith] uncaught render error", error, info.componentStack);
    this.setState({ info });
    // Self-heal a stale-chunk error once (guarded against a reload loop).
    if (isChunkLoadError(error) && typeof sessionStorage !== "undefined") {
      if (!sessionStorage.getItem("emlx:chunkReloaded")) {
        sessionStorage.setItem("emlx:chunkReloaded", "1");
        window.location.reload();
      }
    }
  }

  private buildReport(): string {
    const e = this.state.error;
    return JSON.stringify(
      {
        message: e?.message ?? String(e),
        name: e?.name,
        stack: e?.stack,
        componentStack: this.state.info?.componentStack,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        url: typeof location !== "undefined" ? location.href : "",
      },
      null,
      2,
    );
  }

  /**
   * Copy the report, and when that fails, HAND IT OVER ANOTHER WAY.
   *
   * The lede above this button tells the reader to copy the report to pass
   * on, and this screen is the last one standing: the provider tree it would
   * need for a toast is exactly what may have crashed, so a failure has to be
   * answered in the boundary's own markup. It is answered with the report
   * itself, in a read-only field the reader can select, because saying "copy
   * failed" and stopping leaves them with the same nothing they had.
   */
  private copyReport = () => {
    void writeClipboard(this.buildReport()).then((result) => {
      if (result !== "written") {
        this.setState({ copied: false, copyFailed: true });
        return;
      }
      this.setState({ copied: true, copyFailed: false });
      window.setTimeout(() => this.setState({ copied: false }), 1500);
    });
  };

  // Break a poisoned-localStorage reload loop: clear saved state, then reload.
  private clearAndReload = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      /* storage may be unavailable (private mode), reload anyway */
    }
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    const c = COPY[resolveLocale()];
    return (
      <div className="nx-app">
        <main className="nx-home" id="main">
          <div className="nx-home__inner">
            <span className="nx-eyebrow">
              <span className="nx-eyebrow__dot" aria-hidden="true" />
              {c.eyebrow}
            </span>
            <h1 className="nx-home__title">{c.title}</h1>
            <p className="nx-home__lede">{c.lede}</p>
            <div className="nx-drop__row" style={{ marginTop: "var(--space-4)" }}>
              <button
                type="button"
                className="nx-btn nx-btn--primary"
                onClick={() => window.location.reload()}
              >
                {c.reload}
              </button>
              <button type="button" className="nx-btn" onClick={this.clearAndReload}>
                {c.reset}
              </button>
              <button type="button" className="nx-btn" onClick={this.copyReport}>
                {this.state.copied ? c.copied : c.copyReport}
              </button>
            </div>
            {this.state.copyFailed ? (
              <div className="nx-crash__fallback">
                <p role="status" className="nx-crash__note">
                  {c.copyFailed}
                </p>
                <textarea
                  className="nx-crash__report"
                  aria-label={c.reportLabel}
                  readOnly
                  rows={10}
                  value={this.buildReport()}
                />
              </div>
            ) : null}
          </div>
        </main>
      </div>
    );
  }
}
